import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  HStack,
  Button,
  Select,
  useColorModeValue,
  Spinner,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  CircularProgress,
  CircularProgressLabel,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import {
  FiServer,
  FiDatabase,
  FiCpu,
  FiHardDrive,
  FiUsers,
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiGlobe,
  FiCloud,
  FiLock,
  FiShield,
  FiRefreshCw,
  FiSettings,
  FiTerminal,
  FiBarChart2,
  FiZap,
  FiClock,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertCircle,
  FiCalendar,
  FiMonitor,
  FiWifi,
  FiDownload,
  FiUpload,
  FiSave,
} from 'react-icons/fi';
import {
  MdOutlineStorage,
  MdOutlineDevices,
  MdOutlineNetworkCheck,
  MdSecurity,
  MdBugReport,
  MdOutlineAppSettingsAlt,
  MdPermDeviceInformation,
  MdOutlineUpdate,
  MdOutlineSpeed,
  MdOutlineMemory,
  MdOutlineBackup,
} from 'react-icons/md';
import api from '../../utils/api';

// Reusable components
const StatCard = ({ title, value, icon, change, colorScheme, formatter, isLoading, footerText }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);
  const iconColor = useColorModeValue('white', 'gray.800');

  const displayValue = formatter ? formatter(value) : value;

  return (
    <Card boxShadow="md" bg={cardBg}>
      <CardBody p={4}>
        <Flex alignItems="center">
          <Box
            bg={iconBg}
            color={iconColor}
            p={3}
            borderRadius="lg"
            mr={4}
            boxShadow="md"
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            {isLoading ? (
              <Spinner size="sm" mt={1} colorScheme={colorScheme} />
            ) : (
              <Text fontSize="2xl" fontWeight="bold">
                {displayValue}
              </Text>
            )}
            {change !== undefined && (
              <Stat>
                <StatHelpText mb={0}>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              </Stat>
            )}
            {footerText && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {footerText}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

const formatPercentage = (value) => {
  return `${value}%`;
};

const formatStorage = (value) => {
  if (value < 1024) {
    return `${value} GB`;
  } else {
    return `${(value / 1024).toFixed(1)} TB`;
  }
};

const formatTime = (value) => {
  return `${value}ms`;
};

// Main Dashboard Component
const TIDashboard = () => {
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [systemStats, setSystemStats] = useState({
    serverUptime: { value: 99.98, change: 0.02 },
    avgResponseTime: { value: 187, change: -12 },
    activeUsers: { value: 1258, change: 15 },
    storageUsed: { value: 2150, change: 8 },
    bandwidthUsage: { value: 875, change: 22 },
    securityAlerts: { value: 12, change: -30 },
    openTickets: { value: 28, change: -15 },
    apiCalls: { value: 4235670, change: 25 },
  });
  const [servers, setServers] = useState([]);
  const [services, setServices] = useState([]);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [performanceData, setPerformanceData] = useState({
    days: [],
    responseTime: [],
    cpuUsage: [],
    memoryUsage: [],
  });
  const [deployments, setDeployments] = useState([]);

  useEffect(() => {
    // Simulating API call
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await api.get('api/dashboard/ti');
        // const data = await response.json();
        
        // Mock data for demo
        setTimeout(() => {
          setSystemStats({
            serverUptime: { value: 99.98, change: 0.02 },
            avgResponseTime: { value: 187, change: -12 },
            activeUsers: { value: 1258, change: 15 },
            storageUsed: { value: 2150, change: 8 },
            bandwidthUsage: { value: 875, change: 22 },
            securityAlerts: { value: 12, change: -30 },
            openTickets: { value: 28, change: -15 },
            apiCalls: { value: 4235670, change: 25 },
          });
          
          // Mock servers data
          setServers([
            { 
              id: 1, 
              name: 'Web-Server-01', 
              status: 'online', 
              uptime: '47d 12h', 
              cpu: 34, 
              memory: 62, 
              storage: 48,
              location: 'AWS-USEast',
            },
            { 
              id: 2, 
              name: 'Web-Server-02', 
              status: 'online', 
              uptime: '58d 7h', 
              cpu: 28, 
              memory: 55, 
              storage: 42,
              location: 'AWS-USEast',
            },
            { 
              id: 3, 
              name: 'DB-Server-01', 
              status: 'online', 
              uptime: '23d 5h', 
              cpu: 42, 
              memory: 78, 
              storage: 72,
              location: 'AWS-USEast',
            },
            { 
              id: 4, 
              name: 'Storage-Server-01', 
              status: 'maintenance', 
              uptime: '0d 9h', 
              cpu: 8, 
              memory: 25, 
              storage: 85,
              location: 'AWS-USWest',
            },
            { 
              id: 5, 
              name: 'Cache-Server-01', 
              status: 'online', 
              uptime: '38d 22h', 
              cpu: 65, 
              memory: 70, 
              storage: 23,
              location: 'AWS-USEast',
            },
          ]);
          
          // Mock services data
          setServices([
            {
              id: 1,
              name: 'API Gateway',
              status: 'operational',
              healthScore: 98,
              loadBalanced: true,
              lastIncident: '32d 14h ago',
            },
            {
              id: 2,
              name: 'Authentication Service',
              status: 'operational',
              healthScore: 96,
              loadBalanced: true,
              lastIncident: '12d 8h ago',
            },
            {
              id: 3,
              name: 'Database Cluster',
              status: 'operational',
              healthScore: 95,
              loadBalanced: true,
              lastIncident: '18d 3h ago',
            },
            {
              id: 4,
              name: 'Search Engine',
              status: 'degraded',
              healthScore: 82,
              loadBalanced: true,
              lastIncident: '2d 4h ago',
            },
            {
              id: 5,
              name: 'File Storage Service',
              status: 'operational',
              healthScore: 94,
              loadBalanced: false,
              lastIncident: '45d 7h ago',
            },
            {
              id: 6,
              name: 'Message Queue',
              status: 'operational',
              healthScore: 99,
              loadBalanced: true,
              lastIncident: '67d 9h ago',
            },
          ]);
          
          // Mock security alerts
          setSecurityAlerts([
            {
              id: 1,
              type: 'Brute Force Attempt',
              priority: 'high',
              timestamp: '29/04/2025 - 14:23',
              source: '203.87.123.45',
              status: 'investigating',
            },
            {
              id: 2,
              type: 'Unusual Login Location',
              priority: 'medium',
              timestamp: '28/04/2025 - 22:05',
              source: 'Admin Account',
              status: 'resolved',
            },
            {
              id: 3,
              type: 'Multiple Failed Logins',
              priority: 'low',
              timestamp: '27/04/2025 - 09:47',
              source: 'Mobile App',
              status: 'resolved',
            },
            {
              id: 4,
              type: 'API Rate Limit Exceeded',
              priority: 'medium',
              timestamp: '26/04/2025 - 16:12',
              source: 'Client App #238',
              status: 'resolved',
            },
          ]);
          
          // Mock performance data
          setPerformanceData({
            days: ['25/04', '26/04', '27/04', '28/04', '29/04', '30/04', '01/05'],
            responseTime: [210, 195, 205, 190, 175, 180, 187],
            cpuUsage: [42, 38, 45, 52, 48, 40, 43],
            memoryUsage: [65, 62, 68, 72, 70, 68, 64],
          });
          
          // Mock deployments
          setDeployments([
            {
              id: 1,
              version: 'v3.5.2',
              date: '25/04/2025',
              status: 'successful',
              deployedBy: 'CI/CD Pipeline',
              features: ['API Performance Improvements', 'Bug Fixes', 'New Admin Panel Features'],
            },
            {
              id: 2,
              version: 'v3.5.1',
              date: '18/04/2025',
              status: 'successful',
              deployedBy: 'CI/CD Pipeline',
              features: ['Security Patches', 'Dependency Updates'],
            },
            {
              id: 3,
              version: 'v3.5.0',
              date: '10/04/2025',
              status: 'successful',
              deployedBy: 'Manual Deploy',
              features: ['New Payment Gateway Integration', 'UI Improvements', 'Performance Optimizations'],
            },
          ]);
          
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const cardBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');
  const lightBg = useColorModeValue('gray.50', 'gray.600');
  
  // Status badge colors
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'online':
      case 'operational':
      case 'successful':
        return 'green';
      case 'offline':
      case 'failed':
        return 'red';
      case 'maintenance':
      case 'degraded':
        return 'yellow';
      case 'investigating':
        return 'orange';
      case 'resolved':
        return 'blue';
      default:
        return 'gray';
    }
  };
  
  // Priority colors
  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };
  
  // Resource usage color
  const getResourceColor = (value) => {
    if (value < 50) return 'green';
    if (value < 80) return 'yellow';
    return 'red';
  };

  return (
    <Box w="100%" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Dashboard TI</Heading>
          <Text color="gray.500">Infraestrutura, segurança e monitoramento de sistemas</Text>
        </Box>
        <HStack>
          <Text mr={2}>Período:</Text>
          <Select value={timeFrame} onChange={handleTimeFrameChange} w="150px">
            <option value="day">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </Select>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="yellow" 
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 1000);
            }}
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* System Overview Cards */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Uptime de Servidores" 
          value={systemStats.serverUptime.value} 
          icon={FiServer} 
          change={systemStats.serverUptime.change} 
          colorScheme="green" 
          formatter={formatPercentage}
          isLoading={isLoading} 
          footerText="Disponibilidade média"
        />
        <StatCard 
          title="Tempo de Resposta" 
          value={systemStats.avgResponseTime.value} 
          icon={MdOutlineSpeed} 
          change={systemStats.avgResponseTime.change} 
          colorScheme="blue" 
          formatter={formatTime}
          isLoading={isLoading}
          footerText="Média de API/Backend"
        />
        <StatCard 
          title="Usuários Ativos" 
          value={systemStats.activeUsers.value} 
          icon={FiUsers} 
          change={systemStats.activeUsers.change} 
          colorScheme="purple" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Simultâneos no sistema"
        />
        <StatCard 
          title="Armazenamento Usado" 
          value={systemStats.storageUsed.value} 
          icon={MdOutlineStorage} 
          change={systemStats.storageUsed.change} 
          colorScheme="teal" 
          formatter={formatStorage}
          isLoading={isLoading}
          footerText="Total em todos os servidores"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Uso de Banda" 
          value={systemStats.bandwidthUsage.value} 
          icon={FiActivity} 
          change={systemStats.bandwidthUsage.change} 
          colorScheme="cyan" 
          formatter={(value) => `${value} GB`}
          isLoading={isLoading}
          footerText="Tráfego de rede"
        />
        <StatCard 
          title="Alertas de Segurança" 
          value={systemStats.securityAlerts.value} 
          icon={FiShield} 
          change={systemStats.securityAlerts.change} 
          colorScheme="red" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="No período atual"
        />
        <StatCard 
          title="Tickets Abertos" 
          value={systemStats.openTickets.value} 
          icon={MdBugReport} 
          change={systemStats.openTickets.change} 
          colorScheme="orange" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Suporte técnico"
        />
        <StatCard 
          title="Chamadas de API" 
          value={systemStats.apiCalls.value} 
          icon={FiTerminal} 
          change={systemStats.apiCalls.change} 
          colorScheme="yellow" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Requisições processadas"
        />
      </Grid>

      {/* System Performance & Servers */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Desempenho do Sistema</Heading>
              <HStack>
                <Icon as={FiZap} color="yellow.500" />
                <Icon as={FiBarChart2} color="blue.500" ml={2} />
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Tabs colorScheme="yellow" isFitted>
                <TabList>
                  <Tab><Icon as={FiClock} mr={2} /> Tempo de Resposta</Tab>
                  <Tab><Icon as={FiCpu} mr={2} /> Utilização de Recursos</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Box mb={6}>
                      <Text fontSize="sm" color="gray.500" mb={4} textAlign="center">
                        Tempo de Resposta Médio (últimos 7 dias)
                      </Text>
                      
                      {/* Placeholder for chart - in a real app, use Chart.js or Recharts */}
                      <Box h="250px" position="relative">
                        <Flex direction="column" h="100%" justify="flex-end">
                          <Flex h="100%" align="flex-end">
                            {performanceData.days.map((day, index) => (
                              <Flex 
                                key={index} 
                                direction="column" 
                                h="100%" 
                                flex="1" 
                                align="center" 
                                justify="flex-end"
                                px={1}
                              >
                                <Box
                                  w="100%"
                                  h={`${(performanceData.responseTime[index] / 250) * 100}%`}
                                  bg="blue.300"
                                  borderTopRadius="md"
                                />
                                <Text fontSize="xs" mt={2} fontWeight="medium">
                                  {day}
                                </Text>
                              </Flex>
                            ))}
                          </Flex>
                        </Flex>
                      </Box>
                      
                      <HStack mt={4} spacing={6} justify="center">
                        <HStack>
                          <Box w="12px" h="12px" bg="blue.300" borderRadius="sm" />
                          <Text fontSize="sm">Tempo de Resposta (ms)</Text>
                        </HStack>
                        <Text fontSize="sm" fontWeight="bold">
                          Média: {systemStats.avgResponseTime.value}ms
                        </Text>
                      </HStack>
                    </Box>

                    <Divider my={4} />

                    <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4}>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Tempo de Resposta API</Text>
                        <CircularProgress value={85} size="100px" thickness="8px" color="green.400">
                          <CircularProgressLabel>85%</CircularProgressLabel>
                        </CircularProgress>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          Target: &lt;200ms
                        </Text>
                      </Box>
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Tempo de Resposta BD</Text>
                        <CircularProgress value={78} size="100px" thickness="8px" color="yellow.400">
                          <CircularProgressLabel>78%</CircularProgressLabel>
                        </CircularProgress>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          Target: &lt;50ms
                        </Text>
                      </Box>
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Tempo de Carregamento</Text>
                        <CircularProgress value={92} size="100px" thickness="8px" color="blue.400">
                          <CircularProgressLabel>92%</CircularProgressLabel>
                        </CircularProgress>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          Target: &lt;3s
                        </Text>
                      </Box>
                    </Grid>
                  </TabPanel>
                  
                  <TabPanel>
                    <Text fontSize="sm" color="gray.500" mb={4} textAlign="center">
                      Uso de CPU e Memória (últimos 7 dias)
                    </Text>
                    
                    {/* Placeholder for CPU/Memory chart - in a real app, use Chart.js or Recharts */}
                    <Box h="250px" position="relative" mb={4}>
                      <Flex direction="column" h="100%" justify="flex-end">
                        <Flex h="100%" align="flex-end">
                          {performanceData.days.map((day, index) => (
                            <Flex 
                              key={index} 
                              direction="column" 
                              h="100%" 
                              flex="1" 
                              align="center" 
                              justify="flex-end"
                              px={1}
                            >
                              <Box
                                w="100%"
                                h={`${performanceData.memoryUsage[index]}%`}
                                bg="purple.300"
                                borderTopRadius={performanceData.cpuUsage[index] === 0 ? "md" : "none"}
                              />
                              <Box
                                w="100%"
                                h={`${performanceData.cpuUsage[index]}%`}
                                bg="orange.300"
                                borderTopRadius="md"
                                mt={1}
                              />
                              <Text fontSize="xs" mt={2} fontWeight="medium">
                                {day}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Flex>
                    </Box>
                    
                    <HStack mt={2} spacing={6} justify="center" mb={4}>
                      <HStack>
                        <Box w="12px" h="12px" bg="purple.300" borderRadius="sm" />
                        <Text fontSize="sm">Uso de Memória</Text>
                      </HStack>
                      <HStack>
                        <Box w="12px" h="12px" bg="orange.300" borderRadius="sm" />
                        <Text fontSize="sm">Uso de CPU</Text>
                      </HStack>
                    </HStack>
                    
                    <Divider my={4} />
                    
                    <Stack spacing={4} mt={4}>
                      <Text fontSize="sm" fontWeight="medium">Utilização de Recursos por Serviço</Text>
                      
                      <Box>
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm">API Gateway</Text>
                          <Text fontSize="sm" fontWeight="medium">42%</Text>
                        </Flex>
                        <Progress value={42} size="sm" colorScheme="green" borderRadius="full" />
                      </Box>
                      
                      <Box>
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm">Database Cluster</Text>
                          <Text fontSize="sm" fontWeight="medium">78%</Text>
                        </Flex>
                        <Progress value={78} size="sm" colorScheme="yellow" borderRadius="full" />
                      </Box>
                      
                      <Box>
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm">Search Engine</Text>
                          <Text fontSize="sm" fontWeight="medium">65%</Text>
                        </Flex>
                        <Progress value={65} size="sm" colorScheme="yellow" borderRadius="full" />
                      </Box>
                      
                      <Box>
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm">File Storage</Text>
                          <Text fontSize="sm" fontWeight="medium">85%</Text>
                        </Flex>
                        <Progress value={85} size="sm" colorScheme="red" borderRadius="full" />
                      </Box>
                    </Stack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Status de Servidores</Heading>
              <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                {isLoading ? '-' : '4/5 Online'}
              </Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={3}>
                {servers.map(server => (
                  <Box 
                    key={server.id} 
                    p={3} 
                    borderRadius="md"
                    bg={lightBg}
                    borderLeft="4px solid" 
                    borderLeftColor={getStatusColor(server.status) + '.500'}
                  >
                    <Flex justify="space-between" align="center" mb={2}>
                      <HStack>
                        <Icon as={FiServer} color={getStatusColor(server.status) + '.500'} />
                        <Text fontWeight="medium">{server.name}</Text>
                      </HStack>
                      <Badge colorScheme={getStatusColor(server.status)}>
                        {server.status}
                      </Badge>
                    </Flex>
                    <Grid templateColumns="repeat(4, 1fr)" gap={2} fontSize="sm">
                      <Box>
                        <Text color="gray.500">Uptime</Text>
                        <Text fontWeight="medium">{server.uptime}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">CPU</Text>
                        <Text fontWeight="medium" color={getResourceColor(server.cpu) + '.500'}>
                          {server.cpu}%
                        </Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Mem</Text>
                        <Text fontWeight="medium" color={getResourceColor(server.memory) + '.500'}>
                          {server.memory}%
                        </Text>
                      </Box>
                      <Box>
                        <Text color="gray.500">Disk</Text>
                        <Text fontWeight="medium" color={getResourceColor(server.storage) + '.500'}>
                          {server.storage}%
                        </Text>
                      </Box>
                    </Grid>
                  </Box>
                ))}
                
                <Button 
                  colorScheme="yellow" 
                  variant="outline" 
                  size="sm" 
                  mt={2} 
                  leftIcon={<FiSettings />}
                >
                  Gerenciar Servidores
                </Button>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Services Status & Security Alerts */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "1fr 1fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Status dos Serviços</Heading>
              <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                {isLoading ? '-' : '5/6 Operacionais'}
              </Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={3}>
                {services.map(service => (
                  <Flex 
                    key={service.id} 
                    justify="space-between" 
                    align="center" 
                    p={3} 
                    borderRadius="md"
                    bg={lightBg}
                  >
                    <HStack>
                      <Icon 
                        as={service.status === 'operational' ? FiCheckCircle : FiAlertTriangle} 
                        color={getStatusColor(service.status) + '.500'} 
                      />
                      <Box>
                        <Text fontWeight="medium">{service.name}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {service.loadBalanced ? 'Load Balanced' : 'Single Instance'} | Último incidente: {service.lastIncident}
                        </Text>
                      </Box>
                    </HStack>
                    <HStack>
                      <CircularProgress 
                        value={service.healthScore} 
                        size="40px" 
                        thickness="8px"
                        color={
                          service.healthScore > 90 ? 'green.400' :
                          service.healthScore > 80 ? 'yellow.400' : 'red.400'
                        }
                      >
                        <CircularProgressLabel fontSize="xs">{service.healthScore}</CircularProgressLabel>
                      </CircularProgress>
                      <Badge colorScheme={getStatusColor(service.status)} ml={2}>
                        {service.status}
                      </Badge>
                    </HStack>
                  </Flex>
                ))}
                
                <Button 
                  colorScheme="yellow" 
                  variant="outline" 
                  size="sm" 
                  mt={2} 
                  leftIcon={<FiActivity />}
                >
                  Ver Histórico de Incidentes
                </Button>
              </Stack>
            )}
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Alertas de Segurança</Heading>
              <Badge 
                colorScheme={securityAlerts.filter(a => a.status === 'investigating').length > 0 ? 'red' : 'green'} 
                px={2} 
                py={1} 
                borderRadius="full"
              >
                {isLoading ? '-' : securityAlerts.filter(a => a.status === 'investigating').length + ' Ativos'}
              </Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={3}>
                {securityAlerts.map(alert => (
                  <Box 
                    key={alert.id} 
                    p={3} 
                    borderRadius="md"
                    bg={lightBg}
                    borderLeft="4px solid" 
                    borderLeftColor={getPriorityColor(alert.priority) + '.500'}
                  >
                    <Flex justify="space-between" align="center" mb={1}>
                      <HStack>
                        <Icon as={FiShield} color={getPriorityColor(alert.priority) + '.500'} />
                        <Text fontWeight="medium">{alert.type}</Text>
                      </HStack>
                      <Badge colorScheme={getStatusColor(alert.status)}>
                        {alert.status}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" fontSize="xs" color="gray.500">
                      <Text>Fonte: {alert.source}</Text>
                      <Text>{alert.timestamp}</Text>
                    </Flex>
                    <HStack mt={2} spacing={2}>
                      <Badge colorScheme={getPriorityColor(alert.priority)} size="sm">
                        {alert.priority}
                      </Badge>
                      {alert.status === 'investigating' && (
                        <Badge colorScheme="red" variant="outline" size="sm">
                          Ação necessária
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                ))}
                
                <Button 
                  colorScheme="yellow" 
                  variant="outline" 
                  size="sm" 
                  mt={2} 
                  leftIcon={<MdSecurity />}
                >
                  Centro de Segurança
                </Button>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Recent Deployments & Backups Status */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "2fr 1fr" }} gap={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Heading size="md">Implantações Recentes</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                {deployments.map(deployment => (
                  <Box 
                    key={deployment.id} 
                    p={4} 
                    borderRadius="md"
                    bg={lightBg}
                  >
                    <Flex justify="space-between" align="center" mb={3}>
                      <HStack>
                        <Box
                          bg={getStatusColor(deployment.status) + '.500'}
                          p={2}
                          borderRadius="md"
                          color="white"
                        >
                          <Icon as={FiUpload} />
                        </Box>
                        <Box>
                          <Text fontWeight="bold">{deployment.version}</Text>
                          <Text fontSize="xs" color="gray.500">
                            {deployment.date} por {deployment.deployedBy}
                          </Text>
                        </Box>
                      </HStack>
                      <Badge colorScheme={getStatusColor(deployment.status)}>
                        {deployment.status}
                      </Badge>
                    </Flex>
                    
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>Principais alterações:</Text>
                      <List spacing={1} pl={4} fontSize="sm">
                        {deployment.features.map((feature, index) => (
                          <ListItem key={index}>
                            <ListIcon as={FiCheckCircle} color="green.500" />
                            {feature}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                ))}
                
                <Button 
                  colorScheme="yellow" 
                  variant="outline" 
                  size="sm" 
                  leftIcon={<MdOutlineUpdate />}
                >
                  Ver Histórico Completo
                </Button>
              </Stack>
            )}
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Heading size="md">Status de Backups</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Flex p={4} bg={lightBg} borderRadius="md" align="center">
                  <CircularProgress value={100} size="60px" thickness="8px" color="green.400">
                    <CircularProgressLabel>
                      <Icon as={FiCheckCircle} color="green.500" />
                    </CircularProgressLabel>
                  </CircularProgress>
                  <Box ml={4}>
                    <Text fontWeight="medium">Backup Diário</Text>
                    <Text fontSize="sm" color="gray.500">Último: Hoje, 03:00</Text>
                    <Badge colorScheme="green" mt={1}>Sucesso</Badge>
                  </Box>
                </Flex>
                
                <Flex p={4} bg={lightBg} borderRadius="md" align="center">
                  <CircularProgress value={100} size="60px" thickness="8px" color="green.400">
                    <CircularProgressLabel>
                      <Icon as={FiCheckCircle} color="green.500" />
                    </CircularProgressLabel>
                  </CircularProgress>
                  <Box ml={4}>
                    <Text fontWeight="medium">Backup Semanal</Text>
                    <Text fontSize="sm" color="gray.500">Último: 28/04/2025, 01:00</Text>
                    <Badge colorScheme="green" mt={1}>Sucesso</Badge>
                  </Box>
                </Flex>
                
                <Flex p={4} bg={lightBg} borderRadius="md" align="center">
                  <CircularProgress value={100} size="60px" thickness="8px" color="green.400">
                    <CircularProgressLabel>
                      <Icon as={FiCheckCircle} color="green.500" />
                    </CircularProgressLabel>
                  </CircularProgress>
                  <Box ml={4}>
                    <Text fontWeight="medium">Backup Mensal</Text>
                    <Text fontSize="sm" color="gray.500">Último: 01/04/2025, 00:00</Text>
                    <Badge colorScheme="green" mt={1}>Sucesso</Badge>
                  </Box>
                </Flex>
                
                <Box p={4} bg="blue.50" borderRadius="md">
                  <HStack mb={2}>
                    <Icon as={MdOutlineBackup} color="blue.500" />
                    <Text fontWeight="medium">Política de Retenção</Text>
                  </HStack>
                  <List spacing={1} fontSize="sm" pl={6}>
                    <ListItem>Diário: 7 dias</ListItem>
                    <ListItem>Semanal: 4 semanas</ListItem>
                    <ListItem>Mensal: 12 meses</ListItem>
                  </List>
                </Box>
                
                <Button 
                  colorScheme="yellow" 
                  variant="outline" 
                  size="sm" 
                  leftIcon={<FiSave />}
                >
                  Configurar Backups
                </Button>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
};

export default TIDashboard;